import { Component, type ReactNode } from 'react';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { type RelayAGGError, getFirstAGGError } from './utils.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { RelayAGGError } from './utils';

interface Props {
	children: ReactNode;
	fallback: ReactNode;
	handleError?: (error: Error | RelayAGGError) => void;
}

interface State {
	error: Error | RelayAGGError | null;
}

export class ErrorBoundary extends Component<Props, State> {
	static getDerivedStateFromError = (error: Error) => {
		const aggError = getFirstAGGError(error);

		return {
			error: aggError || error,
		};
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			error: null,
		};
	}

	componentDidCatch() {
		const { handleError } = this.props;
		const { error } = this.state;

		if (error) {
			handleError?.(error);
		}
	}

	render() {
		const { error } = this.state;
		const { children, fallback } = this.props;

		if (!error) {
			return children;
		}

		if (isHttpClientErrorResponse(error)) {
			return fallback;
		}

		// If this isn't a client side error, let the upper error boundary handle it
		throw error;
	}
}
